import { useStyles } from './styles';
import Layout from 'components/Layout';
import { BasicTable, Chip, CircularIndeterminate, DateFormattedUtils, PaginationControlled, PreviewExperiencer, TextInput, Title, useDebounce } from '@utriper/core-ui';
import { useTranslation } from 'react-i18next';
import { Button, Fade, FormControl, Grid2, IconButton, InputAdornment, InputLabel, MenuItem, Paper, Select, Typography, useMediaQuery } from '@mui/material';
import { Add, Check, Close, Edit, Search } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Cost, formatCurrency } from '@utriper/core';
import { PreviewService } from 'core/api/services/preview';
import { useCosts } from 'core/contexts/cost';

const CostsPage = () => {
    const classes = useStyles();
    const { t: translate, i18n } = useTranslation();
    const { loading, costs, costCounts, total, getCosts } = useCosts();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const history = useHistory();

    const [nameSearch, setNameSearch] = useState('');
    const [costTypeFilter, setCostTypeFilter] = useState('all');
    const [page, setPage] = useState(1);
    const nameDebounce = useDebounce(nameSearch, 300);
    const MAX_PAGE = 30;

    const costTypeOptions = useMemo(() => {
        const costArray = [
            ...Cost?.getIds().map((id) => {
                const translated = Cost.getTranslatedName(Cost.types, id, i18n.language);

                let count;

                if (costCounts) {
                    count = costCounts[`type_${id}`];
                }

                return { id, name: translated?.[0] ?? '', count: count ?? 0 };
            }),
        ];

        let count;
        if (costCounts) {
            count = costCounts['total'];
        }

        costArray.unshift({ id: 'all', name: translate('costs.all_types'), count: count ?? 0 });
        return costArray;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language, costCounts]);

    const handlePageChange = (_event, value) => {
        setPage(value);
    };

    const onChangeNameSearch = (event) => {
        setNameSearch(event.target.value);
        if (page > 1) {
            setPage(1);
        }
    };

    const onChangeFilter = (event) => {
        const value = event?.target?.value;
        setCostTypeFilter(value);
        if (page > 1) {
            setPage(1);
        }
    };

    const renderCostCountColor = (type) => {
        switch (type) {
            case 'all':
                return '#137dc5';
            case Cost.IDS.system:
                return '#50c878';
            case Cost.IDS.user:
                return '#e4954b';
            default:
                return '#137dc5';
        }
    };

    const columns = [
        { align: 'left', val: 'id', label: 'ID' },
        { align: 'left', val: 'experiencer', label: translate('costs.columns.experiencer') },
        { align: 'left', val: 'name', label: translate('costs.columns.name'), style: { minWidth: '120px' } },
        { align: 'left', val: 'typeString', label: translate('costs.columns.type') },
        { align: 'left', val: 'value', label: translate('costs.columns.value') },
        { align: 'center', val: 'isSystem', label: translate('costs.columns.system') },
        { align: 'left', val: 'created', label: translate('costs.columns.created'), style: { minWidth: '120px' } },
        { align: 'left', val: 'updated', label: translate('costs.columns.updated'), style: { minWidth: '120px' } },
        { val: 'edit', label: ' ', style: { width: '1px' } },
    ];

    const renderDate = (date) =>
        DateFormattedUtils(date, i18n.language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
        });

    const prettifyCosts = (costs = []) =>
        costs.map((cost) => ({
            ...cost,
            experiencer: <PreviewExperiencer userId={cost.experiencerId} label="name" previewService={PreviewService} />,
            name: (
                <Typography fontWeight="bold" fontSize="1em" color="primary">
                    {cost?.name}
                </Typography>
            ),
            typeString: <Chip style={{ color: renderCostCountColor(cost.type), fontWeight: 'bold' }} label={Cost.getTranslatedName(Cost.types, cost?.type, i18n.language)} />,
            value: cost?.defaultPercentage ? <Typography>{`${cost.defaultPercentage}%`}</Typography> : <Typography>{formatCurrency(cost?.defaultPrice, cost?.defaultCurrency, i18n.language)}</Typography>,
            isSystem: cost?.isSystem ? <Check sx={{ color: '#50c878' }} /> : <Close />,
            created: (
                <Grid2 container flexDirection="column" spacing={1} justifyContent="center" alignItems="flex-start">
                    <Typography fontWeight="bold">{renderDate(cost?.createdOn)}</Typography>
                    <Typography fontSize=".8em">{`${translate('costs.created_by')} ${cost?.createdBy}`}</Typography>
                </Grid2>
            ),
            updated: cost?.updatedOn ? (
                <Grid2 container flexDirection="column" spacing={1} justifyContent="center" alignItems="flex-start">
                    <Typography fontWeight="bold">{renderDate(cost?.updatedOn)}</Typography>
                    <Typography fontSize=".8em">{`${translate('costs.updated_by')} ${cost?.updatedBy}`}</Typography>
                </Grid2>
            ) : (
                ''
            ),
            edit: (
                <IconButton
                    className={classes.tableButton}
                    onClick={() => {
                        history.push(`/cost/${cost?.id}/edit`);
                    }}>
                    <Edit />
                </IconButton>
            ),
        }));

    const onClickRow = (_event, id) => {
        history.push(`/cost/${id}`);
    };

    const onClickAdd = () => {
        history.push('/cost/0/edit');
    };

    useEffect(() => {
        getCosts({
            name: nameDebounce,
            type: costTypeFilter,
            pager: {
                page,
                pageSize: MAX_PAGE,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, nameDebounce, costTypeFilter]);

    return (
        <Layout contentClassName={classes.content}>
            <Title value={translate('costs.title')} />

            <Fade in={true}>
                <Grid2 container spacing={2}>
                    {costTypeOptions?.map(({ id, name, count }) => (
                        <Grid2 size={{ xs: 12, sm: 4 }} key={`key-${id}-${count}`}>
                            <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', padding: 3, borderRadius: 2, boxShadow: 'var(--shadow-card)' }}>
                                <Typography fontSize="2rem" fontWeight="bold" color={renderCostCountColor(id)} sx={{ mb: 1 }}>
                                    {count}
                                </Typography>
                                <Typography fontSize="1rem">{id === 'all' ? translate('costs.total_costs') : name}</Typography>
                            </Paper>
                        </Grid2>
                    ))}
                </Grid2>
            </Fade>

            <Fade in={true}>
                <Grid2 container spacing={2} alignItems="center" justifyContent="space-between" flexWrap={{ xs: 'wrap', md: 'nowrap' }} my={4}>
                    <Grid2 container size={{ xs: 12, md: 8 }} spacing={2} alignItems="center" flexWrap={{ xs: 'wrap', sm: 'nowrap' }}>
                        <Grid2 size={{ xs: 12, sm: 8, md: 6 }}>
                            <TextInput
                                input={{
                                    slotProps: {
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search />
                                                </InputAdornment>
                                            ),
                                        },
                                    },
                                }}
                                meta={{ error: null, touched: null }}
                                onChange={onChangeNameSearch}
                                value={nameSearch}
                                label={translate('costs.search_label')}
                                placeholder={`${translate('costs.search_label')}...`}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12, sm: 4, md: 4 }}>
                            <FormControl fullWidth>
                                <InputLabel id="select-cost-type-filter-label">{translate('costs.type_label')}</InputLabel>
                                <Select id="select-cost-type-filter" labelId="select-cost-type-filter-label" value={costTypeFilter} onChange={onChangeFilter} label={translate('costs.type_label')}>
                                    {costTypeOptions?.map((type) => (
                                        <MenuItem key={type?.id} value={type?.id}>
                                            {type?.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid2>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 4 }} display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} mt={{ xs: 2, md: 0 }}>
                        <Button variant="contained" startIcon={<Add />} onClick={onClickAdd}>
                            {translate('costs.new_cost')}
                        </Button>
                    </Grid2>
                </Grid2>
            </Fade>

            <div style={{ paddingBottom: '2em' }}>
                {loading ? (
                    <CircularIndeterminate styles={{ marginTop: isMobile ? '50vh' : '4.5rem' }} />
                ) : (
                    <Fade in={!loading}>
                        <div>
                            <BasicTable size="small" columns={columns} values={prettifyCosts(costs)} onClick={onClickRow} />
                            <br />
                            <Grid2 container justifyContent="center" alignItems="center">
                                <Grid2>{total / MAX_PAGE < 1 ? null : <PaginationControlled count={Math.ceil(total / MAX_PAGE)} page={page} onChange={handlePageChange} totalItems={total} itemsPerPage={MAX_PAGE} />}</Grid2>
                            </Grid2>
                        </div>
                    </Fade>
                )}
            </div>
        </Layout>
    );
};

export default CostsPage;
